import { Carousel as MantineCarousel } from "@mantine/carousel";

import classNames from "./Carousel.module.css";
import classMobile from "./Mobile.module.css";

import { IconArrowLeft, IconArrowRight } from "@/components/icons";

import type { CarouselProps as MantineCarouselProps } from "@mantine/carousel";
import type { ComponentProps, FC, ReactNode } from "react";

export type CarouselProps = {
  children?: ReactNode;
  isMobile?: boolean;
  pb?: number | string;
  slideProps?: Omit<ComponentProps<typeof MantineCarousel.Slide>, "children">;
  slides?: ReactNode[];
} & Pick<
  MantineCarouselProps,
  | "height"
  | "slideSize"
  | "slideGap"
  | "withControls"
  | "withIndicators"
  | "emblaOptions"
  | "initialSlide"
  | "onSlideChange"
  | "getEmblaApi"
> &
  Omit<MantineCarouselProps, "children">;

type CarouselComponent = FC<CarouselProps> & {
  Slide: typeof MantineCarousel.Slide;
};

export const Carousel: CarouselComponent = ({
  children,
  isMobile = false,
  slides,
  slideProps = { m: "sm" },
  height = "100%",
  slideSize = { base: "calc(100%/2)", sm: "calc(100%/6)", lg: "calc(100%/8)" },
  slideGap = 0,
  withControls = false,
  withIndicators = true,
  pb = 50,
  emblaOptions = { align: "start", slidesToScroll: 1, containScroll: "trimSnaps" },
  ...props
}) => {
  const renderContent = () => {
    if (slides && slides.length > 0) {
      return slides.map((slide, index) => (
        <MantineCarousel.Slide key={index} {...slideProps}>
          {slide}
        </MantineCarousel.Slide>
      ));
    }
    return children;
  };
  const carosuelClassNames = isMobile ? { ...classNames, ...classMobile } : classNames;

  return (
    <MantineCarousel
      height={height}
      slideSize={slideSize}
      slideGap={slideGap}
      withControls={withControls}
      withIndicators={withIndicators}
      classNames={carosuelClassNames}
      pb={pb}
      emblaOptions={emblaOptions}
      previousControlIcon={<IconArrowLeft />}
      previousControlProps={{ style: { borderRadius: "var(--mantine-radius-xs)", opacity: 1 } }}
      nextControlIcon={<IconArrowRight />}
      nextControlProps={{ style: { borderRadius: "var(--mantine-radius-xs)", opacity: 1 } }}
      {...props}
    >
      {renderContent()}
    </MantineCarousel>
  );
};

Carousel.Slide = MantineCarousel.Slide;
